import { useState } from 'react';
import { Button, type ButtonProps } from '@/components/ui/button';
import { Loader2, Play, ArrowRight, UserPlus, Sparkles, Award, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUserAccess } from '@/hooks/useUserAccess';
import { useEnrollSelfPacedRoute } from '@/hooks/useSelfPacedRoutes';
import { useSelectedCohort } from '@/hooks/useSelectedCohort';
import { useToast } from '@/hooks/use-toast';
import { SelfPacedRoutePreviewDialog } from '@/components/public/SelfPacedRoutePreviewDialog';

interface Props {
  programId: string;
  programName?: string;
  description?: string | null;
  enrolled?: boolean;
  progress?: number;
  /** Cohorte perpetua del usuario para esta ruta (fija el filtro en Contenido) */
  cohortId?: string;
  /** El usuario ya tiene certificado emitido de esta ruta */
  hasCertificate?: boolean;
  size?: ButtonProps['size'];
  className?: string;
}

/**
 * CTA de rutas de autoaprendizaje.
 * Visitante → "Regístrate"; sin acceso → modal con el temario + "Suscribirme";
 * con acceso → "Comenzar ahora" / "Continuar · X%" / "Ver certificado".
 */
export function SelfPacedRouteCta({
  programId,
  programName,
  description,
  enrolled,
  progress,
  cohortId,
  hasCertificate,
  size = 'sm',
  className,
}: Props) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const enroll = useEnrollSelfPacedRoute();
  const { hasPaidAccess, isLoading: accessLoading } = useUserAccess();
  const { setSelectedCohortId } = useSelectedCohort();
  const [previewOpen, setPreviewOpen] = useState(false);

  const goToContent = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (cohortId) setSelectedCohortId(cohortId);
    navigate(cohortId ? `/content?cohort=${cohortId}` : '/content');
  };

  if (!user) {
    return (
      <Button size={size} className={className} onClick={e => { e.stopPropagation(); navigate('/auth'); }}>
        <UserPlus className="w-4 h-4 mr-2" />
        Regístrate
      </Button>
    );
  }

  if (hasCertificate) {
    return (
      <Button size={size} className={className} onClick={e => { e.stopPropagation(); navigate('/profile'); }}>
        <Award className="w-4 h-4 mr-2" />
        Ver certificado
      </Button>
    );
  }

  if (enrolled) {
    const completed = progress === 100;
    return (
      <Button size={size} className={className} onClick={goToContent}>
        {completed ? <RotateCcw className="w-4 h-4 mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
        {completed
          ? 'Repasar ruta'
          : typeof progress === 'number' && progress > 0
            ? `Continuar · ${progress}%`
            : 'Continuar'}
      </Button>
    );
  }


  // Usuario logueado sin acceso: llevarlo directamente a suscribirse
  if (!accessLoading && !hasPaidAccess) {
    return (
      <Button
        size={size}
        className={className}
        onClick={e => { e.stopPropagation(); navigate('/profile?tab=pagos'); }}
      >
        <Sparkles className="w-4 h-4 mr-2" />
        Suscribirme
      </Button>
    );
  }

  const handleStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    enroll.mutate(programId, {
      onSuccess: (data) => {
        toast({ title: '¡Ruta activada!', description: 'Ya puedes avanzar a tu ritmo desde Contenido.' });
        const newCohortId = data?.cohort_id ?? cohortId;
        if (newCohortId) setSelectedCohortId(newCohortId);
        navigate(newCohortId ? `/content?cohort=${newCohortId}` : '/content');
      },
      onError: (err: any) => {
        if (err.code === 'no_license') {
          setPreviewOpen(true);
          return;
        }
        toast({ title: 'No pudimos activar la ruta', description: err.message, variant: 'destructive' });
      },
    });
  };

  return (
    <>
      <Button size={size} className={className} onClick={handleStart} disabled={enroll.isPending || accessLoading}>
        {enroll.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
        Comenzar ahora
      </Button>
      <SelfPacedRoutePreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        programId={programId}
        programName={programName ?? 'Ruta de autoaprendizaje'}
        description={description}
      />
    </>
  );
}

export function SelfPacedPlansCta({ size = 'sm', className }: { size?: ButtonProps['size']; className?: string }) {
  const navigate = useNavigate();
  return (
    <Button size={size} variant="outline" className={className} onClick={e => { e.stopPropagation(); navigate('/planes'); }}>
      <Sparkles className="w-4 h-4 mr-2" />
      Ver planes
    </Button>
  );
}

export default SelfPacedRouteCta;
